import { useEffect, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { usePageLayoutResize } from '@atlassian/jira-navigation-system/src/index.tsx';
import { NAVIGATION_UI_STORAGE_KEY } from '@atlassian/jira-router-resources-navigation-ui-state/src/constants.tsx';
import { navigationUiStateResource } from '@atlassian/jira-router-resources-navigation-ui-state/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useLayoutStoreActions, DEFAULT_LEFT_SIDEBAR_WIDTH } from '../../store/index.tsx';

import { useLeftSidebarState } from '../left-sidebar-controller/index.tsx';
import { useRightSidebarState } from '../right-sidebar-controller/index.tsx';
import type { PreviewPanelState, RightSidebarState } from '../../../../common/types.tsx';
import { usePreviewPanelState } from '../preview-panel/index.tsx';
import { setRemoteLayoutState } from './set-remote-layout-state/index.tsx';

const LayoutRemoteStateUpdaterOld = () => {
	// This prevents eccessive state updates on page loads
	const [initialLoad, setInitialLoad] = useState(true);
	const [localLeftSidebarWidthState, setLocalLeftSidebarWidthState] = useState<number>();
	const [localLeftSidebarCollapsedState, setLocalLeftSidebarCollapsedState] = useState<boolean>();
	const [localRightSidebarState, setLocalRightSidebarState] = useState<RightSidebarState>();
	const [localPreviewPanelState, setLocalPreviewPanelState] = useState<PreviewPanelState>();

	// left-sidebar
	const { leftSidebarState } = usePageLayoutResize();
	const { isFlyoutOpen, isLeftSidebarCollapsed, leftSidebarWidth } = leftSidebarState;
	const resolvedLeftSidebarWidth: number = Math.max(leftSidebarWidth, DEFAULT_LEFT_SIDEBAR_WIDTH);
	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const isCollapsed: boolean = leftSidebarState.isLeftSidebarCollapsed;

	// right-sidebar
	const rightSidebarState = useRightSidebarState();
	const accountId = useAccountId();
	const { data, loading, error } = useResource(navigationUiStateResource);
	const isLayoutStateReady = leftSidebarWidth !== 0 && leftSidebarState.lastLeftSidebarWidth !== 0;

	// preview panel
	const previewPanelState = usePreviewPanelState();

	// compares local vs external state coming via chain of callbacks
	// to determine if we really need to send an update of layout state
	const sideBarChanged = useCallback(
		(
			externalLeftSidebarWidth: number,
			externalIsLeftSidebarCollapsed: boolean,
			externalRightSidebarState: RightSidebarState,
			externalPreviewPanelState: PreviewPanelState,
		): boolean => {
			let changeDetected = false;
			if (!isEqual(externalLeftSidebarWidth, localLeftSidebarWidthState)) {
				setLocalLeftSidebarWidthState(externalLeftSidebarWidth);
				changeDetected = true;
			}
			if (!isEqual(externalIsLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
				setLocalLeftSidebarCollapsedState(externalIsLeftSidebarCollapsed);
				changeDetected = true;
			}
			if (!isEqual(externalRightSidebarState, localRightSidebarState)) {
				setLocalRightSidebarState(externalRightSidebarState);
				changeDetected = true;
			}
			if (!isEqual(externalPreviewPanelState, localPreviewPanelState)) {
				setLocalPreviewPanelState(externalPreviewPanelState);
				changeDetected = true;
			}
			return changeDetected;
		},
		[
			localLeftSidebarCollapsedState,
			localLeftSidebarWidthState,
			localRightSidebarState,
			localPreviewPanelState,
		],
	);

	useEffect(() => {
		if (!isLayoutStateReady) return;
		if (error) return;
		setLeftSidebarState({
			width: resolvedLeftSidebarWidth,
			isCollapsed,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			isFlyoutOpen: isFlyoutOpen as boolean,
			isVisible: true,
		});
	}, [
		error,
		isCollapsed,
		isFlyoutOpen,
		isLayoutStateReady,
		resolvedLeftSidebarWidth,
		setLeftSidebarState,
	]);
	const updateData = useCallback(async () => {
		if (loading) return;
		if (error) return;
		if (!isLayoutStateReady) return;
		if (
			!sideBarChanged(
				resolvedLeftSidebarWidth,
				isLeftSidebarCollapsed,
				rightSidebarState,
				previewPanelState,
			)
		)
			return;
		const remoteData = {
			...data,
			leftSidebar: {
				width: resolvedLeftSidebarWidth,
				isCollapsed: isLeftSidebarCollapsed,
			},
			rightSidebar: rightSidebarState,
			...(fg('jira-glance-panel') && { previewPanel: previewPanelState }),
		};

		if (accountId) {
			try {
				await setUserProperties(accountId, NAVIGATION_UI_STORAGE_KEY, JSON.stringify(remoteData));
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					error: e,
					meta: {
						id: 'updateServerLayoutState',
						packageName: 'jiraLayoutController',

						teamName: 'magma',
					},
				});
			}
		}
	}, [
		accountId,
		data,
		error,
		isLayoutStateReady,
		isLeftSidebarCollapsed,
		loading,
		resolvedLeftSidebarWidth,
		rightSidebarState,
		previewPanelState,
		sideBarChanged,
	]);
	useEffect(() => {
		if (initialLoad) {
			setInitialLoad(false);
			setLocalLeftSidebarWidthState(resolvedLeftSidebarWidth);
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
			setLocalRightSidebarState(rightSidebarState);
		} else {
			updateData();
		}
	}, [
		isLeftSidebarCollapsed,
		resolvedLeftSidebarWidth,
		updateData,
		rightSidebarState,
		initialLoad,
	]);
	return null;
};

const LayoutRemoteStateUpdaterNew = () => {
	const accountId = useAccountId();
	const leftSidebar = useLeftSidebarState();
	const rightSidebar = useRightSidebarState();
	const previewPanel = usePreviewPanelState();

	const isLeftSidebarCollapsed = leftSidebar.isCollapsed;
	const leftSidebarWidth = leftSidebar.width;

	// This prevents eccessive state updates on page loads
	const [initialLoad, setInitialLoad] = useState(true);
	const [localLeftSidebarWidthState, setLocalLeftSidebarWidthState] = useState<number>();
	const [localLeftSidebarCollapsedState, setLocalLeftSidebarCollapsedState] = useState<boolean>();
	const [localRightSidebarState, setLocalRightSidebarState] = useState<RightSidebarState>();
	const [localPreviewPanelState, setLocalPreviewPanelState] = useState<PreviewPanelState>();

	// compares local vs external state coming via chain of callbacks
	// to determine if we really need to send an update of layout state
	const sideBarChanged = useCallback(
		(
			externalLeftSidebarWidth: number,
			externalIsLeftSidebarCollapsed: boolean,
			externalRightSidebarState: RightSidebarState,
			externalPreviewPanelState: PreviewPanelState,
		): boolean => {
			let changeDetected = false;
			if (!isEqual(externalLeftSidebarWidth, localLeftSidebarWidthState)) {
				setLocalLeftSidebarWidthState(externalLeftSidebarWidth);
				changeDetected = true;
			}
			if (!isEqual(externalIsLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
				setLocalLeftSidebarCollapsedState(externalIsLeftSidebarCollapsed);
				changeDetected = true;
			}
			if (!isEqual(externalRightSidebarState, localRightSidebarState)) {
				setLocalRightSidebarState(externalRightSidebarState);
				changeDetected = true;
			}
			if (!isEqual(externalPreviewPanelState, localPreviewPanelState)) {
				setLocalPreviewPanelState(externalPreviewPanelState);
				changeDetected = true;
			}
			return changeDetected;
		},
		[
			localLeftSidebarCollapsedState,
			localLeftSidebarWidthState,
			localRightSidebarState,
			localPreviewPanelState,
		],
	);

	useEffect(() => {
		if (!accountId) {
			return;
		}
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (initialLoad) {
			setInitialLoad(false);
			setLocalLeftSidebarWidthState(leftSidebarWidth);
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
			setLocalRightSidebarState(rightSidebar);
			setLocalPreviewPanelState(previewPanel);
			return;
		}
		if (!sideBarChanged(leftSidebarWidth, isLeftSidebarCollapsed, rightSidebar, previewPanel))
			return;

		setRemoteLayoutState({
			accountId,
			state: {
				rightSidebar,
				leftSidebar: {
					isCollapsed: isLeftSidebarCollapsed,
					width: leftSidebarWidth,
				},
				...(fg('jira-glance-panel') && { previewPanel }),
			},
		});
	}, [
		accountId,
		isLeftSidebarCollapsed,
		leftSidebarWidth,
		rightSidebar,
		initialLoad,
		previewPanel,
		sideBarChanged,
	]);

	return null;
};

export const LayoutRemoteStateUpdater = componentWithCondition(
	getWillShowNav4,
	LayoutRemoteStateUpdaterNew,
	LayoutRemoteStateUpdaterOld,
);
